import {
    MainEndpoints,
    UpdateArticleBodyParams,
    ContentResponse,
    ContentWithSharesResponse,
    FindContentMetadataResponse,
    FindContentMetadataBodyParams,
    ModifyCollectionContent,
    CollectionResponse,
    PublicContentShareStatusResponse,
    PublicContentResponseWithStatus,
    RefetchContentBodyParams,
    EmptyResponse
} from "@authory-packages/core";
import axios from "axios";
import { CommonArgs } from "./collections";
import { getHeaders } from "./get-headers";
import { API_URL, withUserSlug } from "./index";

export const getUserExport = async (token: string, userSlug: string) => {
    const { data } = await axios.get<{
        timestamp: string;
        token: string;
        xmlExportUrl: string;
        xmlExportUrlWithoutDomain: string;
        zipExportUrl: string;
        zipExportUrlWithoutDomain: string;
    }>(`${API_URL}${withUserSlug(MainEndpoints.UserExport, userSlug)}`, getHeaders(token));

    return data;
};
interface getItemArgs {
    itemSlug: string;
    token: string | null;
    authorSlug: string;
}

export const getArticle = async ({ token, authorSlug, itemSlug }: getItemArgs) => {
    const stringToReplace = !!authorSlug.length ? ":userId?" : "/:userId?";
    const { data } = await axios.get<ContentWithSharesResponse>(
        `${API_URL}${MainEndpoints.UserArticle.replace(stringToReplace, encodeURIComponent(authorSlug)).replace(":articleId", itemSlug)}`,
        token ? getHeaders(token) : undefined
    );

    return data;
};

interface updateArticleArgs extends CommonArgs {
    data: UpdateArticleBodyParams;
    articleSlug: string;
    target?: string;
}

export const updateArticle = async ({ token, userSlug, data, articleSlug }: updateArticleArgs) => {
    const res = await axios.post<ContentResponse>(
        `${API_URL}${withUserSlug(MainEndpoints.UpdateUserArticle, userSlug).replace(":articleId", encodeURIComponent(articleSlug))}`,
        {
            ...data
        },
        getHeaders(token)
    );

    return res.data;
};

interface refetchArticleArgs extends CommonArgs {
    articleSlug: string;
    data?: RefetchContentBodyParams;
}

/**
 * Queues a re-import of an article that's already in the account. The work happens
 * in a worker afterwards, so the response is empty and the refreshed item isn't available yet.
 */
export const refetchArticle = async ({ token, userSlug, articleSlug, data = {} }: refetchArticleArgs) => {
    const res = await axios.post<EmptyResponse>(
        `${API_URL}${withUserSlug(MainEndpoints.RefetchUserArticle, userSlug).replace(":articleId", encodeURIComponent(articleSlug))}`,
        {
            ...data
        },
        getHeaders(token)
    );

    return res.data;
};

interface deleteArticleArgs extends CommonArgs {
    itemSlug: string;
}

export const deleteArticle = async ({ token, userSlug, itemSlug }: deleteArticleArgs) => {
    const res = await axios.post<ContentResponse>(
        `${API_URL}${withUserSlug(MainEndpoints.DeleteUserArticle, userSlug).replace(":articleId", encodeURIComponent(itemSlug))}`,
        {},
        getHeaders(token)
    );

    return res.data;
};

interface addOrRemoveArticleFromCollectionArgs extends CommonArgs {
    collectionSlug: string;
    payload: ModifyCollectionContent;
    add: boolean;
    skipContentInvalidation?: boolean;
    useContentDetaisOptimisticUpdate?: boolean;
}

export const addOrRemoveArticleFromCollection = async ({ token, userSlug, collectionSlug, payload, add }: addOrRemoveArticleFromCollectionArgs) => {
    const endpoint = add ? MainEndpoints.UserCollectionAddArticle : MainEndpoints.UserCollectionRemoveArticle;

    const res = await axios.post<CollectionResponse>(
        `${API_URL}${withUserSlug(endpoint, userSlug).replace(":collectionId", encodeURIComponent(collectionSlug))}`,
        { ...payload },
        getHeaders(token)
    );

    return res.data;
};

type UploadTemporaryImageArgs = {
    token: string;
    file: File;
    userSlug: string;
    noResize?: boolean;
};

export const uploadTemporaryImage = async ({ token, file, userSlug, noResize = false }: UploadTemporaryImageArgs) => {
    let formData = new FormData();
    formData.append("file", file);

    const params: any = {};
    if (noResize) params.noResize = noResize;

    const { data } = await axios.post<{ url: string }>(
        `${API_URL}${withUserSlug(MainEndpoints.CreateUserArticleTemporaryImage, userSlug)}`,
        formData,
        {
            params,
            headers: {
                ...getHeaders(token).headers,
                "Content-Type": "multipart/form-data"
            }
        }
    );

    return data;
};

interface FindContentSnippetsArgs {
    params: FindContentMetadataBodyParams;
    token: string;
    timeout?: number;
}

export const findContentSnippets = async ({ token, params, timeout }: FindContentSnippetsArgs) => {
    const res = await axios.post<FindContentMetadataResponse>(`${API_URL}${MainEndpoints.FindContentMetadata}`, params, {
        timeout,
        ...getHeaders(token || "")
    });

    return res.data;
};

export const getArticleShareStatus = async (userSlug: string, contentItemResolveKey: string = "", token: string | null) => {
    const { data } = await axios.get<PublicContentShareStatusResponse>(
        `${API_URL}${withUserSlug(MainEndpoints.UserArticlePublicShareStatus, userSlug).replace(":contentItemResolveKey", encodeURIComponent(contentItemResolveKey))}`,
        token ? getHeaders(token) : undefined
    );

    return data;
};

export const getPublicArticle = async (
    userSlug: string,
    contentItemResolveKey: string = "",
    contentItemAccessKey: string = "",
    token: string | null
) => {
    const { data } = await axios.get<PublicContentResponseWithStatus>(
        `${API_URL}${withUserSlug(MainEndpoints.UserArticlePublic, userSlug).replace(":contentItemResolveKey", encodeURIComponent(contentItemResolveKey)).replace(":contentItemAccessKey?", encodeURIComponent(contentItemAccessKey))}`,
        token ? getHeaders(token) : undefined
    );

    return data;
};

export const getPublicSmartShareArticle = async (userSlug: string, smartShareURL: string = "") => {
    const { data } = await axios.get<PublicContentResponseWithStatus>(
        `${API_URL}${withUserSlug(MainEndpoints.UserSmartShareContentItem, userSlug).replace(":smartShareURL", encodeURIComponent(smartShareURL))}`,
        undefined
    );

    return data;
};
